import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Modal, Form, Input, Select, Button, Switch, Flex } from "antd";
import { UserOutlined, MoreOutlined, DeleteOutlined, PhoneOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { FetchPlaceholderFields } from "../../../assets/api/ScriptsService";
import { fetchUserFilters } from "../../../store/filters";
import { getTemplateComponents, EditTemplate } from "../../../assets/api/templateService";
import CustomNotification from "../../../Reuseable/CustomNotification/CustomNotification";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import { country as countryCodes } from "../../../assets/helpers/CountryCodes";
import "./EditTemplateModal.scss";

const { TextArea } = Input;
const { Option } = Select;

// Static country code options mapped outside component scope to avoid recreation on every render
const countryCodeOptions = countryCodes.map((c) => (
  <Option key={`${c.iso}-${c.code}`} value={`${c.iso}_+${c.code}`}>
    {`${c.country} +${c.code}(${c.iso})`}
  </Option>
));

// Static rules for matching country codes sorted by code length descending
const matchRules = (() => {
  const rules = [];
  countryCodes.forEach((c) => {
    const codeWithDash = `+${c.code}`;
    const codeWithoutDash = `+${c.code.replace(/-/g, "")}`;
    
    rules.push({ code: codeWithDash, rawCode: `${c.iso}_+${c.code}` });
    if (codeWithoutDash !== codeWithDash) {
      rules.push({ code: codeWithoutDash, rawCode: `${c.iso}_+${c.code}` });
    }
  });
  rules.sort((a, b) => b.code.length - a.code.length);
  return rules;
})();

const EditTemplateModal = ({ isOpen, onClose, templateData, onSave }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const { filter_list } = useSelector((state) => state.filters) || {};

  const [bodyText, setBodyText] = useState("");
  const [isBodyExpanded, setIsBodyExpanded] = useState(false);
  const [footerText, setFooterText] = useState("");
  const [headerText, setHeaderText] = useState("");
  const [headerType, setHeaderType] = useState("text");
  const [submitting, setSubmitting] = useState(false);

  const [languageOptions, setLanguageOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [maxButtons, setMaxButtons] = useState(3);

  const [placeholderFields, setPlaceholderFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedHeaderPlaceholder, setSelectedHeaderPlaceholder] = useState(null);
  const [selectedBodyPlaceholder, setSelectedBodyPlaceholder] = useState(null);
  const [selectedFooterPlaceholder, setSelectedFooterPlaceholder] = useState(null);

  const [ctas, setCtas] = useState([]);

  const dynamicPortfoliosOptions = useMemo(() => {
    const portfoliosData = filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return portfoliosData.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  // Fetch API options on mount
  useEffect(() => {
    if (token && isOpen) {
      setLoadingComponents(true);
      getTemplateComponents(token)
        .then((res) => {
          if (res?.data?.data) {
            const data = res.data.data;
            if (Array.isArray(data.languages)) {
              setLanguageOptions(data.languages.map((l) => ({ value: l.code, label: l.label })));
            }
            if (Array.isArray(data.categories)) {
              setCategoryOptions(data.categories.map((c) => ({ value: c.value, label: c.label })));
            }
            if (data.buttons?.max_count) {
              setMaxButtons(data.buttons.max_count);
            }
          }
        })
        .catch((err) => console.error("Error fetching template components:", err))
        .finally(() => setLoadingComponents(false));
    }
  }, [token, isOpen]);

  // Fetch placeholders
  useEffect(() => {
    if (token && isOpen) {
      setLoadingFields(true);
      FetchPlaceholderFields(token)
        .then((res) => {
          let dataArray = [];
          if (res?.data?.data?.field_names && Array.isArray(res.data.data.field_names)) {
            dataArray = res.data.data.field_names;
          } else if (res?.data?.field_names && Array.isArray(res.data.field_names)) {
            dataArray = res.data.field_names;
          } else if (Array.isArray(res?.data)) {
            dataArray = res.data;
          } else if (Array.isArray(res?.data?.data)) {
            dataArray = res.data.data;
          }

          const formatLabel = (str) => {
            if (!str) return "";
            return str
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(" ");
          };

          const options = dataArray.map((item) => {
            const strVal = typeof item === "string" ? item : item?.name || item?.key || String(item);
            return { value: strVal, label: formatLabel(strVal) };
          });

          setPlaceholderFields(options);
        })
        .catch((err) => console.error("Error fetching placeholders:", err))
        .finally(() => setLoadingFields(false));
    }
  }, [token, isOpen]);

  // Ensure filters are loaded
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchUserFilters());
    }
  }, [dispatch, isOpen]);

  // Prepopulate form when templateData changes
  useEffect(() => {
    if (isOpen && templateData) {
      const headerComp = templateData.components?.find((c) => c.type === "HEADER");
      const bodyComp = templateData.components?.find((c) => c.type === "BODY");
      const footerComp = templateData.components?.find((c) => c.type === "FOOTER");
      const buttonsComp = templateData.components?.find((c) => c.type === "BUTTONS");

      form.setFieldsValue({
        name: templateData.name,
        language: templateData.language,
        portfolio: templateData.portfolio_id,
        category: templateData.category,
        templateType: "default",
        is_active: templateData.is_active ?? false,
        header: headerComp?.text || "",
        body: bodyComp?.text || "",
        footer: footerComp?.text || "",
      });

      setHeaderText(headerComp?.text || "");
      setHeaderType(headerComp?.format?.toLowerCase() || "text");
      setBodyText(bodyComp?.text || "");
      setFooterText(footerComp?.text || "");

      if (buttonsComp && Array.isArray(buttonsComp.buttons)) {
        setCtas(
          buttonsComp.buttons.map((btn, idx) => {
            let code = "+91";
            let number = "";
            if (btn.phone_number) {
              let matched = false;
              for (const rule of matchRules) {
                if (btn.phone_number.startsWith(rule.code)) {
                  code = rule.rawCode;
                  number = btn.phone_number.substring(rule.code.length);
                  matched = true;
                  break;
                }
              }
              if (!matched) {
                number = btn.phone_number;
              }
            }
            return {
              id: idx,
              label: btn.text || "",
              code: code,
              number: number,
            };
          })
        );
      } else {
        setCtas([]);
      }
    } else {
      form.resetFields();
      setHeaderText("");
      setBodyText("");
      setFooterText("");
      setCtas([]);
    }
  }, [isOpen, templateData, form]);

  const handleHeaderPlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentHeader = form.getFieldValue("header") || "";
    const placeholderText = `{{xbucketdata.${val}}}`;
    if (currentHeader.length + placeholderText.length > 120) {
      CustomNotification.show("Placeholder cannot be added as it exceeds the 120 characters limit.", "error");
      setSelectedHeaderPlaceholder(null);
      return;
    }
    const newHeader = currentHeader + placeholderText;
    form.setFieldsValue({ header: newHeader });
    setHeaderText(newHeader);
    setSelectedHeaderPlaceholder(null);
  }, [form]);

  const handleBodyPlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentBody = form.getFieldValue("body") || "";
    const placeholderText = `{{xbucketdata.${val}}}`;
    if (currentBody.length + placeholderText.length > 1024) {
      CustomNotification.show("Placeholder cannot be added as it exceeds the 1024 characters limit.", "error");
      setSelectedBodyPlaceholder(null);
      return;
    }
    const newBody = currentBody + placeholderText;
    form.setFieldsValue({ body: newBody });
    setBodyText(newBody);
    setSelectedBodyPlaceholder(null);
  }, [form]);

  const handleFooterPlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentFooter = form.getFieldValue("footer") || "";
    const placeholderText = `{{xbucketdata.${val}}}`;
    if (currentFooter.length + placeholderText.length > 120) {
      CustomNotification.show("Placeholder cannot be added as it exceeds the 120 characters limit.", "error");
      setSelectedFooterPlaceholder(null);
      return;
    }
    const newFooter = currentFooter + placeholderText;
    form.setFieldsValue({ footer: newFooter });
    setFooterText(newFooter);
    setSelectedFooterPlaceholder(null);
  }, [form]);

  const onFinish = useCallback(async (values) => {
    try {
      setSubmitting(true);

      const payload = {};

      // 1. Check Name
      if (values.name !== templateData?.name) {
        payload.name = values.name;
      }

      // 2. Check Language
      if (values.language !== templateData?.language) {
        payload.language = values.language;
      }

      // 3. Check Portfolio
      if (values.portfolio && Number(values.portfolio) !== Number(templateData?.portfolio_id)) {
        payload.portfolio_id = Number(values.portfolio);
      }

      // 4. Check Category
      if (values.category !== templateData?.category) {
        payload.category = values.category;
      }

      // 5. Check Status (is_active)
      if (values.is_active !== undefined && !!values.is_active !== !!templateData?.is_active) {
        payload.is_active = !!values.is_active;
      }

      // 6. Check Components
      const headerComp = templateData?.components?.find((c) => c.type === "HEADER");
      const bodyComp = templateData?.components?.find((c) => c.type === "BODY");
      const footerComp = templateData?.components?.find((c) => c.type === "FOOTER");
      const buttonsComp = templateData?.components?.find((c) => c.type === "BUTTONS");

      const payloadComponents = [];

      // Check Header Change
      const currentHeader = values.header || "";
      const originalHeader = headerComp?.text || "";
      if (headerType === "text" && currentHeader !== originalHeader) {
        payloadComponents.push({
          type: "HEADER",
          format: "TEXT",
          text: currentHeader,
        });
      }

      // Check Body Change
      const currentBody = values.body || "";
      const originalBody = bodyComp?.text || "";
      if (currentBody !== originalBody) {
        payloadComponents.push({
          type: "BODY",
          text: currentBody,
        });
      }

      // Check Footer Change
      const currentFooter = values.footer || "";
      const originalFooter = footerComp?.text || "";
      if (currentFooter !== originalFooter) {
        payloadComponents.push({
          type: "FOOTER",
          text: currentFooter,
        });
      }

      // Check Buttons (CTAs) Change
      const originalButtons = buttonsComp?.buttons || [];
      const currentButtons = ctas.map((cta) => ({
        type: "PHONE_NUMBER",
        text: cta.label || "Phone",
        phone_number: `${cta.code ? (cta.code.includes('_') ? cta.code.split('_')[1] : cta.code) : ""}${cta.number || ""}`,
      }));

      // Compare original buttons with current buttons
      let buttonsChanged = false;
      if (currentButtons.length !== originalButtons.length) {
        buttonsChanged = true;
      } else {
        for (let i = 0; i < currentButtons.length; i++) {
          if (
            currentButtons[i].text !== originalButtons[i].text ||
            currentButtons[i].phone_number !== originalButtons[i].phone_number
          ) {
            buttonsChanged = true;
            break;
          }
        }
      }

      if (buttonsChanged) {
        payloadComponents.push({
          type: "BUTTONS",
          buttons: currentButtons,
        });
      }

      // Add components to payload if any changed
      if (payloadComponents.length > 0) {
        payload.components = payloadComponents;
      }

      // If no changes at all, notify and close
      if (Object.keys(payload).length === 0) {
        CustomNotification.show("No changes detected", "info");
        onClose();
        return;
      }

      const res = await EditTemplate(templateData.id, token, payload);
      if (res.status === 200 || res.status === 201) {
        CustomNotification.show("Template updated successfully", "success");
        onSave();
        onClose();
      }
    } catch (err) {
      CustomNotification.show(err || "Failed to update template", "error");
    } finally {
      setSubmitting(false);
    }
  }, [headerType, ctas, templateData, token, onClose, onSave]);

  const handleAddButton = useCallback((val) => {
    if (val === "phone" && ctas.length < maxButtons) {
      setCtas(prevCtas => [...prevCtas, { id: Date.now(), label: "", code: "IN_+91", number: "" }]);
    }
  }, [ctas.length, maxButtons]);

  const handleCtaChange = useCallback((index, field, value) => {
    setCtas(prevCtas => {
      const newCtas = [...prevCtas];
      newCtas[index] = { ...newCtas[index], [field]: value };
      return newCtas;
    });
  }, []);

  const handleDeleteCta = useCallback((index) => {
    setCtas(prevCtas => prevCtas.filter((_, i) => i !== index));
  }, []);

  return (
    <Modal
      title="Edit Template"
      open={isOpen}
      onCancel={onClose}
      width={1100}
      footer={null}
      centered
      className="editTemplateModalMain"
    >
      <Flex wrap="wrap" gap={24} className="edit-template-flex-layout" style={{ height: "100%" }}>
        {/* Left Section - Form */}
        <div className="left-section">
          <div className="editTemplateFormWrapper">
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Flex wrap="wrap" gap={16} className="formFieldsGrid">
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[{ required: true, message: "Please enter template name" }]}
                >
                  <Input placeholder="Enter Name" />
                </Form.Item>
                <Form.Item
                  label="Language"
                  name="language"
                  rules={[{ required: true, message: "Please select language" }]}
                >
                  <Select
                    placeholder="Select Language"
                    options={languageOptions}
                    loading={loadingComponents}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Portfolio"
                  name="portfolio"
                  rules={[{ required: true, message: "Please select portfolio" }]}
                >
                  <Select placeholder="Select Portfolio" options={dynamicPortfoliosOptions} allowClear />
                </Form.Item>
                <Form.Item
                  label="Category"
                  name="category"
                  rules={[{ required: true, message: "Please select category" }]}
                >
                  <Select
                    placeholder="Select Category"
                    options={categoryOptions}
                    loading={loadingComponents}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Template Type"
                  name="templateType"
                  initialValue="default"
                  rules={[{ required: true, message: "Please select template type" }]}
                >
                  <Select placeholder="Default">
                    <Option value="default">Default</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Status"
                  name="is_active"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </Flex>

              <div className="textAreasSection">
                {/* Header Section */}
                <div className="custom-header-section" style={{ marginBottom: "16px" }}>
                  <Flex wrap="wrap" justify="space-between" align="center" gap={12} style={{ marginBottom: "8px" }}>
                    <label style={{ color: "#212330", fontWeight: 500 }}>Header</label>
                    <Flex wrap="wrap" gap={8} style={{ flex: '1 1 auto', justifyContent: 'flex-end' }}>
                      <Select
                        showSearch
                        placeholder="Select Placeholder"
                        style={{ flex: "1 1 150px", maxWidth: "300px" }}
                        options={placeholderFields}
                        loading={loadingFields}
                        value={selectedHeaderPlaceholder}
                        onChange={handleHeaderPlaceholderSelect}
                        allowClear
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                      />
                      <Select value={headerType} onChange={(value) => setHeaderType(value)} style={{ flex: "1 1 130px", maxWidth: "200px" }}>
                        <Option value="text">Text (Default)</Option>
                        <Option value="image" disabled>
                          Image
                        </Option>
                        <Option value="document" disabled>
                          Document
                        </Option>
                        <Option value="video" disabled>
                          Video
                        </Option>
                      </Select>
                    </Flex>
                  </Flex>
                  <Form.Item name="header" style={{ marginBottom: 0 }}>
                    {headerType === "text" ? (
                      <TextArea
                        rows={2}
                        maxLength={120}
                        onChange={(e) => setHeaderText(e.target.value)}
                        placeholder="Add header..."
                      />
                    ) : (
                      <div
                        style={{
                          border: "1px dashed #d9d9d9",
                          padding: "24px",
                          textAlign: "center",
                          borderRadius: "6px",
                          background: "#fafafa",
                        }}
                      >
                        <p style={{ margin: 0, color: "#888" }}>Upload is disabled for edit</p>
                      </div>
                    )}
                  </Form.Item>
                  {headerType === "text" && (
                    <div style={{ textAlign: "right", fontSize: "12px", color: "#888", marginTop: "4px" }}>
                      {headerText.length}/120
                    </div>
                  )}
                </div>

                {/* Body Section */}
                <div className="custom-body-section" style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <label className="ant-form-item-required" style={{ color: "#212330", fontWeight: 500 }}>
                      Body
                    </label>
                    <Select
                      showSearch
                      placeholder="Select Placeholder"
                      style={{ width: "150px" }}
                      options={placeholderFields}
                      loading={loadingFields}
                      value={selectedBodyPlaceholder}
                      onChange={handleBodyPlaceholderSelect}
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </div>
                  <Form.Item
                    name="body"
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: "Please enter body text" }]}
                  >
                    <TextArea
                      rows={4}
                      maxLength={1024}
                      onChange={(e) => setBodyText(e.target.value)}
                      placeholder="Enter the text for the template in the appropriate language"
                    />
                  </Form.Item>
                  <div style={{ textAlign: "right", fontSize: "12px", color: "#888", marginTop: "4px" }}>
                    {bodyText.length}/1024
                  </div>
                </div>

                {/* Footer Section */}
                <div className="custom-footer-section" style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <label style={{ color: "#212330", fontWeight: 500 }}>Footer</label>
                    <Select
                      showSearch
                      placeholder="Select Placeholder"
                      style={{ width: "150px" }}
                      options={placeholderFields}
                      loading={loadingFields}
                      value={selectedFooterPlaceholder}
                      onChange={handleFooterPlaceholderSelect}
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </div>
                  <Form.Item name="footer" style={{ marginBottom: 0 }}>
                    <TextArea
                      rows={2}
                      maxLength={120}
                      onChange={(e) => setFooterText(e.target.value)}
                      placeholder="Add a short line to the bottom of your message"
                    />
                  </Form.Item>
                  <div style={{ textAlign: "right", fontSize: "12px", color: "#888", marginTop: "4px" }}>
                    {footerText.length}/120
                  </div>
                </div>

                {/* Buttons Section */}
                <div style={{ marginBottom: "24px" }}>
                  <div style={{ marginBottom: "8px", color: "#212330", fontWeight: 500 }}>Buttons</div>
                  <Select
                    value={null}
                    placeholder="Add Button"
                    style={{ width: "150px" }}
                    disabled={ctas.length >= maxButtons}
                    onChange={handleAddButton}
                    dropdownStyle={{ width: "200px" }}
                  >
                    <Option value="phone">Phone Number</Option>
                  </Select>
                  {ctas.length >= maxButtons && (
                    <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
                      Maximum {maxButtons} buttons allowed
                    </div>
                  )}

                  {ctas.map((cta, index) => (
                    <div
                      key={cta.id}
                      style={{
                        marginTop: "16px",
                        border: "1px solid #1890ff",
                        borderRadius: "8px",
                        padding: "16px",
                        background: "#fff",
                      }}
                    >
                      <div style={{ marginBottom: "12px", fontWeight: "bold", color: "#333" }}>
                        Call To Action {index + 1}
                      </div>
                      <Flex wrap="wrap" gap={12} align="center">
                        <Input
                          placeholder="Phone"
                          value={cta.label}
                          onChange={(e) => handleCtaChange(index, "label", e.target.value)}
                          style={{ flex: "1 1 120px" }}
                        />
                        <Input
                          value="PHONE_NUMBER"
                          disabled
                          style={{ flex: "1 1 140px", backgroundColor: "#f5f5f5", color: "#888" }}
                        />
                        <Select
                          showSearch
                          value={cta.code}
                          onChange={(val) => handleCtaChange(index, "code", val)}
                          style={{ flex: "1 1 160px" }}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {countryCodeOptions}
                        </Select>
                        <Input
                          placeholder="1234567890"
                          maxLength={10}
                          value={cta.number}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(/\D/g, "");
                            handleCtaChange(index, "number", numericValue);
                          }}
                          style={{ flex: "1 1 120px" }}
                        />
                        <Button
                          type="text"
                          icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />}
                          onClick={() => handleDeleteCta(index)}
                        />
                      </Flex>
                    </div>
                  ))}
                </div>
              </div>

              <Flex wrap="wrap" gap={12} className="editTemplateSubmitAction" style={{ marginTop: "24px", width: "100%" }}>
                <NormalButton
                  type="default"
                  onClick={onClose}
                  className="editTemplateCancelBtn"
                  width="130px"
                >
                  Cancel
                </NormalButton>
                <NormalButton
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  className="editTemplateSaveBtn"
                  width="130px"
                >
                  Save Changes
                </NormalButton>
              </Flex>
            </Form>
          </div>
        </div>

        {/* Right Section - WhatsApp Preview */}
        <div className="right-section">
          <div className="whatsappMockupWrapper">
            <div className="whatsapp-mockup">
              <div className="whatsapp-header">
                <div className="whatsapp-header-left">
                  <div className="whatsapp-avatar">
                    <UserOutlined style={{ color: "#075E54", fontSize: "18px" }} />
                  </div>
                  <span className="whatsapp-name">WhatsApp Business Account</span>
                </div>
                <MoreOutlined style={{ fontSize: "18px", color: "white" }} />
              </div>
              <div className="whatsapp-body">
                <div className="whatsapp-date">
                  {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    maxWidth: "280px",
                    alignSelf: "flex-start",
                  }}
                >
                  <div
                    className="whatsapp-message-bubble"
                    style={{
                      display: headerText || bodyText || footerText || ctas.length > 0 ? "block" : "none",
                      padding: 0,
                    }}
                  >
                    <div style={{ padding: "8px 8px 20px 8px" }}>
                      {headerText && headerType === "text" && (
                        <div
                          className="whatsapp-message-header"
                          style={{ fontWeight: "600", marginBottom: "8px", color: "#000" }}
                        >
                          {headerText}
                        </div>
                      )}
                      {bodyText && (
                        <div className="whatsapp-message-body" style={{ whiteSpace: "pre-wrap", fontWeight: "normal" }}>
                          {bodyText.length > 150 ? (
                            <>
                              {isBodyExpanded ? bodyText : `${bodyText.substring(0, 150)}... `}
                              <span 
                                style={{ color: '#027eb5', cursor: 'pointer', fontWeight: 500 }} 
                                onClick={() => setIsBodyExpanded(!isBodyExpanded)}
                              >
                                {isBodyExpanded ? ' show less' : ' show more'}
                              </span>
                            </>
                          ) : (
                            bodyText
                          )}
                        </div>
                      )}
                      {footerText && (
                        <div
                          className="whatsapp-message-footer"
                          style={{ fontStyle: "italic", marginTop: "8px", whiteSpace: "pre-wrap" }}
                        >
                          {footerText}
                        </div>
                      )}
                    </div>

                    {ctas.map((cta) => (
                      <div key={cta.id} style={{ padding: "0 8px 8px 8px" }}>
                        <div
                          style={{
                            border: "1px solid #00a884",
                            padding: "8px",
                            textAlign: "center",
                            color: "#00a884",
                            fontWeight: 500,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                          }}
                        >
                          <PhoneOutlined style={{ fontSize: "16px" }} />
                          {cta.label || "Phone"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Flex>
    </Modal>
  );
};

export default memo(EditTemplateModal);
